import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscriptionService';

const SubscriptionContext = createContext({});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadSubscription();
    } else {
      setSubscription(null);
      setHasActiveSubscription(false);
      setLoading(false);
    }
  }, [user?.id]);

  const loadSubscription = async () => {
    setLoading(true);
    try {
      const { data } = await subscriptionService?.getSubscription(user?.id);
      setSubscription(data);
      setHasActiveSubscription(data?.status === 'active' || data?.status === 'trialing');
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAccess = (requiredTier) => {
    if (!userProfile) return false;
    
    // Check if user has required tier
    if (requiredTier === 'pro') {
      return userProfile?.tier === 'pro' && hasActiveSubscription;
    }
    
    return true; // Free tier always has access to free features
  };

  const value = {
    subscription,
    loading,
    hasActiveSubscription,
    checkAccess,
    refreshSubscription: loadSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
